"use client"

import CustomizedSnackbar from "@/components/Fields/CustomizedSnackbar"
import type React from "react"
import { createContext, useContext, useState, useCallback } from "react"

interface SnackbarContextType {
  showSnackbar: (message: string, severity: "success" | "error" | "warning" | "info") => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning" | "info">("info")

  const showSnackbar = useCallback((message: string, severity: "success" | "error" | "warning" | "info") => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }, [])

  const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (reason === "clickaway") {
      return
    }

    setSnackbarOpen(false)
  }, [])

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <CustomizedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleClose}
      />
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = () => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider")
  }
  return context
}
